/**
 * API Error Notifications
 * 
 * Utility for showing user-friendly error notifications when API calls fail.
 * Integrates with the toast notification system.
 */

import { toast } from "@/hooks/use-toast";

/**
 * Sanitize endpoint name for display
 * Removes leading slash and converts to readable format
 */
function sanitizeEndpoint(endpoint: string): string {
  // Remove leading slash and query parameters
  let cleanEndpoint = endpoint.replace(/^\//, '').split('?')[0];
  
  // Convert kebab-case or snake_case to Title Case
  cleanEndpoint = cleanEndpoint
    .replace(/[-_]/g, ' ')
    .split('/')
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' / ');
  
  return cleanEndpoint || 'API';
}

/**
 * Determine if error is a network error
 */
function isNetworkError(error: string): boolean {
  const networkErrorPatterns = [
    'network error',
    'failed to fetch',
    'network request failed',
    'timeout',
    'connection',
  ];
  
  const lowerError = error.toLowerCase();
  return networkErrorPatterns.some(pattern => lowerError.includes(pattern));
}

/**
 * Show API error notification
 */
export function showApiErrorNotification(
  endpoint: string,
  error: string,
  isRetry: boolean = false
) {
  const endpointName = sanitizeEndpoint(endpoint);
  const isNetwork = isNetworkError(error);
  
  let title = 'API Request Failed';
  let description = error;
  
  if (isRetry) {
    title = 'Request Failed After Retry';
    description = `${endpointName}: ${error}`;
  } else if (isNetwork) {
    title = 'Network Error';
    description = `Unable to reach server (${endpointName})`;
  } else {
    description = `${endpointName}: ${error}`;
  }
  
  toast({
    title,
    description,
    variant: 'destructive',
    duration: 5000,
  });
  
  console.error(`[API Error] ${endpoint}:`, error);
}

/**
 * Show blocked request notification (when request is blocked due to recent failure)
 */
export function showBlockedRequestNotification(endpoint: string) {
  const endpointName = sanitizeEndpoint(endpoint);
  
  toast({
    title: 'Request Temporarily Blocked',
    description: `${endpointName} recently failed. Please wait before retrying.`,
    variant: 'destructive',
    duration: 3000,
  });
  
  console.warn(`[API Blocked] ${endpoint}: Request blocked due to recent failure`);
}

